import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Flex,
  Input,
  Select,
  Text,
} from '@retracedgmbh/ui-components-lib';
import { useEffect, useState } from 'react';
import { useQuery } from '@/hooks/useApi';
import useNetworkCompaniesList from '@/hooks/useNetworkCompaniesList';
import useTranslator from '@/hooks/useTranslator';
import { fetchAllDocumentTypesList } from '@/services/api/document-types';

export interface AssignDocumentData {
  fileName: string;
  companyId: string;
  documentTypeId: string;
  fileId: string;
}

interface AssignDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileName: string;
  fileId: string;
  workflowMetadata: Record<string, unknown> | null;
  onSubmit: (data: AssignDocumentData) => void;
  onGoToDocument: (data: AssignDocumentData) => void;
  isSubmitting: boolean;
}

interface ClassificationResult {
  companyId?: string;
  documentTypeId?: string;
}

function getClassificationResult(
  metadata: Record<string, unknown> | null,
): ClassificationResult {
  if (!metadata) return {};
  const classification = metadata.DOCUMENT_TYPE_CLASSIFICATION as
    | Record<string, unknown>
    | undefined;
  if (!classification) return {};
  return {
    companyId: (classification.companyId as string) ?? undefined,
    documentTypeId: (classification.documentTypeId as string) ?? undefined,
  };
}

export default function AssignDocumentDialog({
  open,
  onOpenChange,
  fileName: initialFileName,
  fileId,
  workflowMetadata,
  onSubmit,
  onGoToDocument,
  isSubmitting,
}: AssignDocumentDialogProps) {
  const [fileName, setFileName] = useState(initialFileName);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [documentTypeId, setDocumentTypeId] = useState<string | null>(null);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const { getTranslation } = useTranslator({ page: 'ai-processing' });

  const companies = useNetworkCompaniesList();

  const { data: documentTypes = [] } = useQuery({
    queryKey: ['document-types-dropdown'],
    queryFn: () => fetchAllDocumentTypesList({ sort: 'title:ASC' }),
    gcTime: 600_000,
  });

  const companyOptions = (companies || []).map(
    (c: { id: string; name: string }) => ({ key: c.id, text: c.name }),
  );
  const documentTypeOptions = documentTypes.map(d => ({
    key: d.id,
    text: d.title,
  }));

  useEffect(() => {
    if (!open) return;
    setFileName(initialFileName);
    const ai = getClassificationResult(workflowMetadata);
    setCompanyId(ai.companyId ?? null);

    // AI returns a label like "ENVIRONMENTAL_POLICY", match against standards by title
    if (ai.documentTypeId && documentTypes.length) {
      const normalized = ai.documentTypeId.toLowerCase().replace(/_/g, ' ');
      const match = documentTypes.find(
        d => d.title.toLowerCase() === normalized || d.id === ai.documentTypeId,
      );
      setDocumentTypeId(match?.id ?? null);
    } else {
      setDocumentTypeId(null);
    }
  }, [open, workflowMetadata, documentTypes]);

  const isValid = fileName.trim() && companyId && documentTypeId;

  const buildData = (): AssignDocumentData => ({
    fileName: fileName.trim(),
    companyId: companyId!,
    documentTypeId: documentTypeId!,
    fileId,
  });

  const handleCancelClick = () => {
    setConfirmCancelOpen(true);
  };

  const handleConfirmCancel = () => {
    setConfirmCancelOpen(false);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog
        open={open && !confirmCancelOpen}
        onOpenChange={isOpen => {
          if (!isOpen) {
            handleCancelClick();
            return;
          }
          onOpenChange(isOpen);
        }}
      >
        <DialogContent>
          <DialogHeader className='tw:mb-4'>
            <DialogTitle className='tw:mb-4'>
              {getTranslation('assignDocument.title')}
            </DialogTitle>
            <Text as='p' size='small' color='secondary'>
              {getTranslation('assignDocument.description')}
            </Text>
          </DialogHeader>

          <Flex direction='column' gap='xl' paddingY='2xl'>
            <Flex direction='column' gap='xs'>
              <Text as='label' size='small' weight='semibold'>
                {getTranslation('assignDocument.fileName')}{' '}
                <Text as='span' className='tw:text-content-negative'>
                  *
                </Text>
              </Text>
              <Input
                value={fileName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFileName(e.target.value)
                }
              />
            </Flex>

            <Flex gap='md'>
              <Flex direction='column' gap='xs' className='tw:flex-1'>
                <Text as='label' size='small' weight='semibold'>
                  {getTranslation('assignDocument.company')}{' '}
                  <Text as='span' className='tw:text-content-negative'>
                    *
                  </Text>
                </Text>
                <Select
                  options={companyOptions}
                  value={
                    companyId
                      ? (companyOptions.find(
                          (o: { key: string }) => o.key === companyId,
                        ) ?? null)
                      : null
                  }
                  onChange={(opt: { key: string }) => setCompanyId(opt.key)}
                  placeholder={getTranslation(
                    'assignDocument.companyPlaceholder',
                  )}
                  width='100%'
                  isSearchable
                />
              </Flex>

              <Flex direction='column' gap='xs' className='tw:flex-1'>
                <Text as='label' size='small' weight='semibold'>
                  {getTranslation('assignDocument.documentType')}{' '}
                  <Text as='span' className='tw:text-content-negative'>
                    *
                  </Text>
                </Text>
                <Select
                  options={documentTypeOptions}
                  value={
                    documentTypeId
                      ? (documentTypeOptions.find(
                          (o: { key: string }) => o.key === documentTypeId,
                        ) ?? null)
                      : null
                  }
                  onChange={(opt: { key: string }) =>
                    setDocumentTypeId(opt.key)
                  }
                  placeholder={getTranslation(
                    'assignDocument.documentTypePlaceholder',
                  )}
                  width='100%'
                  isSearchable
                />
              </Flex>
            </Flex>
            <Flex
              padding='xl'
              marginY='xl'
              gap='sm'
              alignItems='start'
              className='tw:rounded-md tw:border tw:border-orange-500 tw:bg-orange-100'
            >
              <i className='fa-solid fa-circle-exclamation tw:text-yellow-700 tw:mt-0.5' />
              <Text size='small' className='tw:text-amber-800'>
                {getTranslation('assignDocument.warning')}
              </Text>
            </Flex>
          </Flex>

          <DialogFooter>
            <Flex justify='between' alignItems='center' className='tw:w-full'>
              <Button variant='ghost' onClick={handleCancelClick}>
                {getTranslation('assignDocument.cancel')}
              </Button>
              <Flex gap='md'>
                <Button
                  disabled={!isValid || isSubmitting}
                  onClick={() => isValid && onGoToDocument(buildData())}
                >
                  {getTranslation('assignDocument.shareAndGoToDocument')}
                </Button>
                <Button
                  variant='primary'
                  disabled={!isValid || isSubmitting}
                  loading={isSubmitting}
                  onClick={() => isValid && onSubmit(buildData())}
                >
                  {getTranslation('assignDocument.shareAndClose')}
                </Button>
              </Flex>
            </Flex>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmCancelOpen} onOpenChange={setConfirmCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className='tw:mb-xl'>
              {getTranslation('cancelSharing.title')}
            </DialogTitle>
          </DialogHeader>
          <Text as='p' color='secondary' marginY='xl'>
            {getTranslation('cancelSharing.description')}
          </Text>
          <DialogFooter className='tw:gap-md'>
            <Button variant='ghost' onClick={() => setConfirmCancelOpen(false)}>
              {getTranslation('cancelSharing.goBack')}
            </Button>
            <Button variant='primary' onClick={handleConfirmCancel}>
              {getTranslation('cancelSharing.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
